// Copyright (C) 2024 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import { AnyAction } from 'redux';
import { LabelingRunsActionTypes } from 'actions/labeling-runs-actions';
import { LabelingRunsState, ActiveRunState } from '.';

const defaultRunsState: LabelingRunsState = {
    fetching: false,
    count: 0,
    current: [],
};

const defaultActiveRunState: ActiveRunState = {
    fetching: false,
    run: null,
    tasks: [],
    qaStates: {},
};

export function labelingRunsReducer(
    state: LabelingRunsState = defaultRunsState,
    action: AnyAction,
): LabelingRunsState {
    switch (action.type) {
        case LabelingRunsActionTypes.GET_RUNS:
            return { ...state, fetching: true };
        case LabelingRunsActionTypes.GET_RUNS_SUCCESS:
            return {
                ...state,
                fetching: false,
                current: action.payload.runs,
                count: action.payload.count,
            };
        case LabelingRunsActionTypes.GET_RUNS_FAILED:
            return { ...state, fetching: false };
        case LabelingRunsActionTypes.CREATE_RUN_SUCCESS:
            return {
                ...state,
                current: [action.payload.run, ...state.current],
                count: state.count + 1,
            };
        case LabelingRunsActionTypes.DELETE_RUN_SUCCESS:
            return {
                ...state,
                current: state.current.filter((r) => r.id !== action.payload.runId),
                count: state.count - 1,
            };
        case LabelingRunsActionTypes.UPDATE_RUN_SUCCESS:
            return {
                ...state,
                current: state.current.map((r) => (r.id === action.payload.runId ? action.payload.run : r)),
            };
        case LabelingRunsActionTypes.DUPLICATE_RUN_SUCCESS:
            return {
                ...state,
                current: [action.payload.run, ...state.current],
                count: state.count + 1,
            };
        default:
            return state;
    }
}

export function activeRunReducer(
    state: ActiveRunState = defaultActiveRunState,
    action: AnyAction,
): ActiveRunState {
    switch (action.type) {
        case LabelingRunsActionTypes.GET_RUN_DETAIL:
            return { ...state, fetching: true };
        case LabelingRunsActionTypes.GET_RUN_DETAIL_SUCCESS: {
            const qaStates: Record<number, string> = {};
            for (const qa of action.payload.qaStates) {
                qaStates[qa.job_id] = qa.state;
            }
            return {
                ...state,
                fetching: false,
                run: action.payload.run,
                tasks: action.payload.tasks,
                qaStates,
            };
        }
        case LabelingRunsActionTypes.GET_RUN_DETAIL_FAILED:
            return { ...state, fetching: false };
        case LabelingRunsActionTypes.ADD_TASKS_TO_RUN_SUCCESS:
            return {
                ...state,
                tasks: [...state.tasks, ...action.payload.tasks],
            };
        case LabelingRunsActionTypes.REMOVE_TASK_FROM_RUN_SUCCESS:
            return {
                ...state,
                tasks: state.tasks.filter((t: any) => t.task_id !== action.payload.taskId),
            };
        case LabelingRunsActionTypes.UPDATE_JOB_QA_STATE_SUCCESS:
            return {
                ...state,
                qaStates: {
                    ...state.qaStates,
                    [action.payload.jobId]: action.payload.state,
                },
            };
        default:
            return state;
    }
}
